import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  IconButton,
  Slide,
  SlideProps,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import './EmbryoImageLightbox.css';
import { useTranslation } from 'react-i18next';

interface EmbryoImageLightboxProps {
  imageUrl: string | null;
  embryoNumber?: number | string | null;
  onClose: () => void;
  label?: string | null;
}

const Transition = React.forwardRef<unknown, SlideProps>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EmbryoImageLightbox: React.FC<EmbryoImageLightboxProps> = ({ imageUrl, embryoNumber, onClose, label }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');

  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    // reset states whenever image changes
    setZoom(1);

    if (!imageUrl) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const img = new Image();
    img.src = imageUrl;

    img.onload = () => setLoading(false);
    img.onerror = () => setLoading(false); // prevent infinite spinner
  }, [imageUrl]);

  const handleZoom = (direction: 'in' | 'out') => {
    setZoom((prevZoom) => {
      const newZoom = direction === 'in' ? prevZoom * 1.2 : prevZoom / 1.2;
      return Math.min(Math.max(newZoom, 1), 3);
    });
  };

  const titleText = label
    ? label
    : embryoNumber != null
      ? `${tGeneral('embryo')} ${embryoNumber}`
      : tGeneral('embryo');

  return (
    <Dialog
      open={Boolean(imageUrl)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={Transition}
      className="embryo-dialog"
      aria-labelledby="embryo-dialog-title"
      aria-describedby="embryo-dialog-description"
    >
      <DialogTitle id="embryo-dialog-title" className="dialog-title">
        <span className="embryo-number">{titleText}</span>
        <IconButton onClick={onClose} size="small" aria-label={tGeneral('close')} className="close-button">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="dialog-content">
        {loading ? (
          <CircularProgress className="loading-indicator" />
        ) : (
          <img
            src={imageUrl || ''}
            alt={titleText}
            className="embryo-image"
            style={{ transform: `scale(${zoom})` }}
          />
        )}
      </DialogContent>

      <DialogActions className="dialog-actions" sx={{ justifyContent: 'center' }}>
        <Tooltip title={tSurgery('zoomOut')} arrow>
          <IconButton onClick={() => handleZoom('out')} aria-label={tSurgery('zoomOut')} className="zoom-btn" sx={{ color: 'white' }}>
            <RemoveCircleOutlineIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={tSurgery('zoomIn')} arrow>
          <IconButton onClick={() => handleZoom('in')} aria-label={tSurgery('zoomIn')} className="zoom-btn" sx={{ color: 'white' }}>
            <AddCircleOutlineIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title={tGeneral('close')} arrow>
          <Button onClick={onClose} variant="contained" className="close-btn">
            {tGeneral('close')}
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default EmbryoImageLightbox;